import React, {
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Modal, Form } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import './PasswordOtpModal.component.css'

const OTP_LEN = 6
const DIGIT_ONLY = /^\d$/

type OtpModalProps = {
  isOpen: boolean
  email: string
  submitting?: boolean
  resending?: boolean
  errorText?: string
  onRequestClose: () => void
  onVerify: (code: string) => void
  onResend: () => void
}

const emptyDigits = (): string[] => Array<string>(OTP_LEN).fill('')

const maskEmail = (email: string): string => {
  const [local, domain] = email.split('@')
  if (!local || !domain) return email
  if (local.length <= 2) return `${local[0] ?? ''}***@${domain}`
  return `${local.slice(0, 2)}***@${domain}`
}

const PasswordOtpModal: React.FC<OtpModalProps> = ({
  isOpen,
  email,
  submitting = false,
  resending = false,
  errorText,
  onRequestClose,
  onVerify,
  onResend,
}) => {
  const [digits, setDigits] = useState<string[]>(emptyDigits())
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (!isOpen) return
    setDigits(emptyDigits())
    const first = inputsRef.current[0]
    const id = window.setTimeout(() => first?.focus(), 50)
    return () => window.clearTimeout(id)
  }, [isOpen])

  const code = useMemo(() => digits.join(''), [digits])
  const isComplete = code.length === OTP_LEN && digits.every((d) => d !== '')

  const setDigitAt = useCallback((index: number, value: string): void => {
    setDigits((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const focusAt = useCallback((index: number): void => {
    const el = inputsRef.current[index]
    if (el) el.focus()
  }, [])

  const handleChange = useCallback(
    (index: number, raw: string): void => {
      const ch = (raw || '').trim().slice(-1)
      if (ch && !DIGIT_ONLY.test(ch)) return
      setDigitAt(index, ch)
      if (ch && index < OTP_LEN - 1) focusAt(index + 1)
    },
    [focusAt, setDigitAt],
  )

  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Backspace') {
        if (digits[index]) {
          setDigitAt(index, '')
          return
        }
        if (index > 0) {
          setDigitAt(index - 1, '')
          focusAt(index - 1)
        }
        return
      }
      if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault()
        focusAt(index - 1)
      }
      if (e.key === 'ArrowRight' && index < OTP_LEN - 1) {
        e.preventDefault()
        focusAt(index + 1)
      }
      if (e.key === 'Enter' && isComplete && !submitting) {
        e.preventDefault()
        onVerify(code)
      }
    },
    [code, digits, focusAt, isComplete, onVerify, setDigitAt, submitting],
  )

  const handlePaste = useCallback(
    (index: number, e: ClipboardEvent<HTMLInputElement>): void => {
      const pasted = (e.clipboardData?.getData('text') || '')
        .replace(/\D/g, '')
        .slice(0, OTP_LEN)
      if (!pasted) return
      e.preventDefault()
      setDigits((prev) => {
        const next = [...prev]
        for (let i = 0; i < pasted.length; i += 1) {
          const pos = index + i
          if (pos >= OTP_LEN) break
          next[pos] = pasted[i] ?? ''
        }
        return next
      })
      const nextFocus = Math.min(index + pasted.length, OTP_LEN - 1)
      focusAt(nextFocus)
    },
    [focusAt],
  )

  const handleVerify = useCallback((): void => {
    if (!isComplete || submitting) return
    onVerify(code)
  }, [code, isComplete, onVerify, submitting])

  return (
    <Modal
      show={isOpen}
      onHide={onRequestClose}
      size="sm"
      className="custom-modal-width password-otp-modal"
      backdrop="static"
      centered
    >
      <Modal.Header className="bg-custom3 mb-3" closeButton>
        <Modal.Title>OTP modal</Modal.Title>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <Form onSubmit={(e): void => e.preventDefault()}>
          <p className="otp-instruction">
            Enter the 6 OTP number we&apos;ve sent to your email
            {email ? (
              <>
                {' '}
                <span className="otp-email">({maskEmail(email)})</span>
              </>
            ) : (
              '.'
            )}
          </p>

          <div className="otp-input-row">
            {digits.map((d, i) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                ref={(el): void => {
                  inputsRef.current[i] = el
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                className="otp-input"
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={(e) => handlePaste(i, e)}
                aria-label={`Digit ${i + 1} of ${OTP_LEN}`}
                disabled={submitting}
              />
            ))}
          </div>

          {errorText && (
            <div className="otp-error" role="alert">
              {errorText}
            </div>
          )}

          <div className="otp-resend">
            Didn&apos;t get a code?{' '}
            <button
              type="button"
              className="otp-resend-link"
              onClick={onResend}
              disabled={resending || submitting}
            >
              {resending ? 'Resending…' : 'Resend code'}
            </button>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end">
        <Button
          type="button"
          size="md"
          className="button-secondary me-2"
          onClick={onRequestClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="md"
          className="button-primary"
          onClick={handleVerify}
          disabled={!isComplete || submitting}
        >
          {submitting ? 'Verifying…' : 'Verify'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PasswordOtpModal
